'use client';

import { cn } from 'v5/platform/dom/cn';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
  PopoverTrigger
} from './Popover';
import {
  InputGroup,
  InputGroupButton,
  InputGroupInput,
  type InputGroupVariant,
  type InputGroupSize
} from './InputGroup';
import {
  CalendarDate,
  createCalendar,
  getLocalTimeZone,
  isToday
} from '@internationalized/date';
import {
  useCalendar,
  useCalendarCell,
  useCalendarGrid
} from '@react-aria/calendar';
import { useDateField, useDateSegment } from '@react-aria/datepicker';
import { useFocusRing } from '@react-aria/focus';
import { AriaButton } from './_common/AriaButton';
import { useLocale } from '@react-aria/i18n';
import { useCalendarState } from '@react-stately/calendar';
import {
  type DateFieldState,
  useDateFieldState
} from '@react-stately/datepicker';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon
} from 'lucide-react';
import React, { DOMAttributes, Fragment, useRef, useState } from 'react';
import mergeRefs from 'merge-refs';
import { cva } from 'class-variance-authority';

// Helper function to convert CalendarDate to Date
function calendarDateToDate(calendarDate: CalendarDate): Date {
  return new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day);
}

// Helper function to convert Date to CalendarDate
function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate()
  );
}

// Custom DateSegment component using React Aria hooks
function DateSegment({
  segment,
  className,
  state,
  size,
  onOpenCalendar
}: {
  segment: Parameters<typeof useDateSegment>[0];
  className?: string;
  state: DateFieldState;
  size: InputGroupSize;
  onOpenCalendar?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  // Hide literal segments that are empty or only whitespace
  const isEmptyLiteral =
    segment.type === 'literal' && (!segment.text || segment.text.trim() === '');

  return (
    <div
      {...segmentProps}
      ref={ref}
      onKeyDown={e => {
        if (e.key === ' ') {
          e.preventDefault();
          onOpenCalendar?.();
          return;
        }
        segmentProps.onKeyDown?.(e);
      }}
      className={cn(
        className,
        segment.type === 'literal' && 'pointer-events-none px-1',
        segment.isPlaceholder && 'text-fg-muted italic',
        isEmptyLiteral && 'hidden'
      )}
      style={{
        ...segmentProps.style,
        minWidth:
          segment.maxValue != null
            ? String(segment.maxValue).length + 'ch'
            : undefined
      }}
    >
      {segment.isPlaceholder ? segment.placeholder : segment.text}
    </div>
  );
}

// Custom CalendarCell component using React Aria hooks
function CalendarCell({
  date,
  className,
  state
}: {
  date: CalendarDate;
  className?: (state: {
    date: CalendarDate;
    isSelected: boolean;
    isOutsideMonth: boolean;
    isDisabled: boolean;
    isFocusVisible: boolean;
    isSelectionStart: boolean;
    isSelectionEnd: boolean;
  }) => string;
  state: ReturnType<typeof useCalendarState>;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } =
    useCalendarCell({ date }, state, ref);

  const isOutsideMonth = state.isCellUnavailable(date);
  const { focusProps, isFocusVisible } = useFocusRing();

  const cellState = {
    date,
    isSelected,
    isOutsideMonth,
    isDisabled,
    isFocusVisible,
    isSelectionStart: false,
    isSelectionEnd: false
  };

  return (
    <div {...cellProps}>
      <button
        {...buttonProps}
        {...focusProps}
        ref={ref}
        className={className?.(cellState)}
      >
        {formattedDate}
      </button>
    </div>
  );
}

// Custom CalendarGrid component
function CalendarGrid({
  children,
  gridProps
}: {
  children: React.ReactNode;
  gridProps?: DOMAttributes<HTMLElement> & { className?: string };
}) {
  const { className, ...restProps } = gridProps || {};
  return (
    <div {...restProps} className={cn('w-full', className)}>
      {children}
    </div>
  );
}

// Custom CalendarGridHeader component
function CalendarGridHeader({
  children,
  headerProps,
  weekDays
}: {
  children: (day: string) => React.ReactNode;
  headerProps?: DOMAttributes<HTMLElement> & { className?: string };
  weekDays?: string[];
}) {
  const defaultWeekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = weekDays || defaultWeekDays;
  const { className, ...restProps } = headerProps || {};

  return (
    <div
      {...restProps}
      className={cn('grid grid-cols-7 gap-px mb-1', className)}
    >
      {days.map(day => (
        <Fragment key={day}>{children(day)}</Fragment>
      ))}
    </div>
  );
}

// Custom CalendarGridBody component
function CalendarGridBody({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-7 gap-0">{children}</div>;
}

// Custom CalendarHeaderCell component
function CalendarHeaderCell({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

const segmentVariants = cva(
  [
    'focus:outline-none focus:bg-brand-200/50',
    'font-variant-numeric:tabular-nums',
    'text-align:end'
  ],
  {
    variants: {
      size: {
        xs: 'text-sm rounded-sm px-0',
        sm: 'text-base py-0.5 rounded-sm px-0.5 -mx-0.25',
        md: 'text-base py-0.5 rounded-sm px-1 -mx-0.5',
        lg: 'text-lg py-1 rounded px-1 -mx-0.5',
        xl: 'text-xl py-2 rounded px-1 -mx-0.5'
      }
    }
  }
);

export interface DateInputProps {
  value?: CalendarDate | null;
  onChange?: (value: CalendarDate | null) => void;
  size?: InputGroupSize;
  variant?: InputGroupVariant;
  className?: string;
  readOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
  minValue?: CalendarDate;
  maxValue?: CalendarDate;
}

export const DateInput = React.forwardRef<HTMLDivElement, DateInputProps>(
  (
    {
      value,
      onChange,
      size = 'md',
      variant = 'regular',
      className,
      readOnly,
      placeholder,
      disabled,
      minValue,
      maxValue,
      ...props
    },
    ref
  ) => {
    const { locale } = useLocale();
    const [open, setOpen] = useState(false);

    // Date field state for the input
    const fieldState = useDateFieldState({
      value: value ?? undefined,
      onChange: date => {
        onChange?.(date ?? null);
        setOpen(false);
      },
      locale,
      createCalendar,
      isReadOnly: readOnly,
      isDisabled: disabled || readOnly,
      minValue,
      maxValue,
      shouldForceLeadingZeros: true
    });

    const fieldRef = useRef<HTMLDivElement>(null);
    const { fieldProps } = useDateField(
      {
        'aria-label': 'Date',
        ...props
      },
      fieldState,
      fieldRef
    );

    // Calendar state
    // Use focusedValue to ensure the calendar focuses the selected date when it opens
    // This is the proper React Aria way to control which date is focused
    const calendarState = useCalendarState({
      value: value ?? undefined,
      onChange: date => {
        onChange?.(date ?? null);
        setOpen(false);
      },
      visibleDuration: { months: 1 },
      locale,
      createCalendar,
      minValue,
      maxValue,
      focusedValue: open ? (value ?? undefined) : undefined
    });

    const calendarRef = useRef<HTMLDivElement>(null);
    const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(
      {
        'aria-label': 'Calendar',
        autoFocus: true
      },
      calendarState
    );

    const { gridProps, headerProps, weekDays, weeksInMonth } = useCalendarGrid(
      { weekdayStyle: 'short', firstDayOfWeek: 'sun' },
      calendarState
    );

    // Calendar cell styling
    const calendarCellClassName = (state: {
      date: CalendarDate;
      isSelected: boolean;
      isOutsideMonth: boolean;
      isDisabled: boolean;
      isFocusVisible: boolean;
      isSelectionStart: boolean;
      isSelectionEnd: boolean;
    }) => {
      const baseClasses = cn(
        'h-9 w-9 text-center text-sm p-0 rounded relative transition-colors',
        'flex items-center justify-center',
        'border-none',
        // Default state
        'text-fg-default',
        // Hover state
        !state.isDisabled && 'hover:bg-bg-subtle',
        // Focus state
        state.isFocusVisible &&
          'outline-2 outline-brand-300 outline-offset-2 z-10',
        // Selected state
        state.isSelected && 'bg-brand-500 text-white',
        state.isSelected && !state.isDisabled && 'hover:bg-brand-600',
        // Today state - add a dot indicator at the bottom
        isToday(state.date, getLocalTimeZone()) && [
          "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:pointer-events-none",
          state.isSelected ? 'after:bg-white' : 'after:bg-brand-500'
        ],
        // Outside month state
        state.isOutsideMonth && 'text-fg-muted opacity-50',
        // Disabled state
        state.isDisabled &&
          'text-fg-muted opacity-50 cursor-not-allowed hover:bg-transparent'
      );

      return baseClasses;
    };

    const mergedFieldRef = mergeRefs(fieldRef, ref);

    return (
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverAnchor asChild>
          <InputGroup
            variant={variant}
            size={size}
            className={cn('overflow-hidden justify-between w-fit', className)}
            ref={mergedFieldRef}
            aria-readonly={readOnly}
            onDoubleClick={e => {
              if (disabled || readOnly) {
                return;
              }
              if (!open) {
                e.stopPropagation();
                setOpen(true);
              }
            }}
          >
            <InputGroupInput asChild className="flex-1 flex items-center">
              <div {...fieldProps} ref={fieldRef}>
                {fieldState.segments.map((segment, i) => (
                  <DateSegment
                    key={i}
                    segment={segment}
                    state={fieldState}
                    size={size}
                    className={segmentVariants({ size })}
                    onOpenCalendar={() => {
                      if (!disabled && !readOnly && !open) {
                        setOpen(true);
                      }
                    }}
                  />
                ))}
              </div>
            </InputGroupInput>
            {/* Read-only shows no calendar, as a read-only select shows no chevron. */}
            {!readOnly && (
              <PopoverTrigger asChild>
                <InputGroupButton
                  aria-expanded={open}
                  onClick={() => {
                    if (!disabled) {
                      setOpen(true);
                    }
                  }}
                  tabIndex={-1}
                  disabled={disabled}
                >
                  <CalendarIcon />
                </InputGroupButton>
              </PopoverTrigger>
            )}
          </InputGroup>
        </PopoverAnchor>
        <PopoverContent className="p-3 w-auto min-w-0" align="start">
          <div {...calendarProps} ref={calendarRef}>
            <header className="flex items-center justify-between mb-4">
              <AriaButton
                {...prevButtonProps}
                variant="ghost"
                size="sm"
                layout="icon"
                className="rounded p-1"
              >
                <ChevronLeft className="w-4 h-4" />
              </AriaButton>
              <div className="flex items-center gap-2 flex-1 justify-center text-fg-default">
                <span className="text-sm font-medium">
                  {calendarState.visibleRange.start &&
                    new Date(
                      calendarState.visibleRange.start.year,
                      calendarState.visibleRange.start.month - 1,
                      1
                    ).toLocaleDateString(locale, {
                      month: 'long',
                      year: 'numeric'
                    })}
                </span>
              </div>
              <AriaButton
                {...nextButtonProps}
                variant="ghost"
                size="sm"
                layout="icon"
                className="rounded p-1"
              >
                <ChevronRight className="w-4 h-4" />
              </AriaButton>
            </header>
            <CalendarGrid gridProps={gridProps}>
              <CalendarGridHeader headerProps={headerProps} weekDays={weekDays}>
                {day => (
                  <CalendarHeaderCell className="text-fg-muted text-[0.8rem] font-normal text-center w-9 h-9 flex items-center justify-center">
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {Array.from({ length: weeksInMonth }, (_, weekIndex) =>
                  calendarState
                    .getDatesInWeek(weekIndex)
                    .map((date, dayIndex) =>
                      date ? (
                        <CalendarCell
                          key={`${weekIndex}-${dayIndex}`}
                          date={date}
                          state={calendarState}
                          className={calendarCellClassName}
                        />
                      ) : (
                        <div
                          key={`${weekIndex}-${dayIndex}`}
                          className="h-9 w-9"
                        />
                      )
                    )
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

DateInput.displayName = 'DateInput';
